import React, { useEffect, useState } from 'react';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import { collection, getDocs } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useColorMode } from '../ThemeContext';
import { db, appCheckHeaders } from '../firebase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? '';

interface Property {
  id: string;
  name?: string;
  address?: string;
}

export default function Dashboard() {
  const { user, signOut, getIdToken } = useAuth();
  const navigate = useNavigate();
  const { mode, toggleMode } = useColorMode();
  const [properties, setProperties] = useState<Property[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [formName, setFormName] = useState('');
  const [formAddress, setFormAddress] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'clients', user.uid, 'properties'));
        if (cancelled) return;
        setProperties(
          snap.docs.map((d) => {
            const data = d.data() as Partial<Property>;
            return { id: d.id, name: data.name, address: data.address };
          }),
        );
      } catch (err: any) {
        if (!cancelled) setError(err?.message ?? 'Failed to load properties');
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const openDialog = () => {
    setFormName('');
    setFormAddress('');
    setFormError(null);
    setDialogOpen(true);
  };

  const handleAddProperty = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = formName.trim();
    const address = formAddress.trim();
    if (!name) { setFormError('Name is required'); return; }
    if (!address) { setFormError('Address is required'); return; }
    setSubmitting(true);
    setFormError(null);
    try {
      const token = await getIdToken();
      const res = await fetch(`${BACKEND_URL}/properties`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(await appCheckHeaders()),
        },
        body: JSON.stringify({ name, address }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error ?? `Error ${res.status}`);
      const newProp: Property = { id: body.propertyId, name, address };
      setProperties((prev) => [...(prev ?? []), newProp]);
      setDialogOpen(false);
    } catch (err: any) {
      setFormError(err?.message ?? 'Failed to create property');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Top nav */}
      <AppBar position="static">
        <Toolbar>
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 400,
              fontSize: '1.25rem',
              letterSpacing: '-0.01em',
            }}
          >
            Utilibill
          </Typography>
          {user?.email && (
            <Typography
              variant="body2"
              sx={{ mr: 1.5, color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem' }}
            >
              {user.email}
            </Typography>
          )}
          <Tooltip title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
            <IconButton
              color="inherit"
              onClick={toggleMode}
              aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              sx={{ opacity: 0.8, mr: 0.5, '&:hover': { opacity: 1 } }}
            >
              {mode === 'dark' ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Settings">
            <IconButton
              color="inherit"
              onClick={() => navigate('/settings')}
              aria-label="Settings"
              sx={{ opacity: 0.8, mr: 0.5, '&:hover': { opacity: 1 } }}
            >
              <SettingsOutlinedIcon />
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            onClick={() => void signOut()}
            sx={{
              color: 'rgba(255,255,255,0.7)',
              fontSize: '0.8rem',
              '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
            }}
          >
            Sign out
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ pt: 5, pb: 8 }}>
        {/* Page header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', mb: 4 }}>
          <Box>
            <Typography
              variant="h3"
              sx={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 300, mb: 0.5 }}
            >
              Properties
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {properties === null
                ? 'Loading…'
                : `${properties.length} propert${properties.length === 1 ? 'y' : 'ies'}`}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openDialog}
            size="large"
          >
            Add property
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
        )}

        {/* Loading */}
        {properties === null && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
            <CircularProgress size={32} thickness={3} />
          </Box>
        )}

        {/* Empty state */}
        {properties !== null && properties.length === 0 && (
          <Box sx={{
            textAlign: 'center',
            py: 10,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 3,
            bgcolor: 'background.paper',
          }}>
            <HomeWorkOutlinedIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2, opacity: 0.4 }} />
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 500 }}>
              No properties yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 320, mx: 'auto' }}>
              Add your first property to start uploading water bills and managing tenants.
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openDialog}>
              Add your first property
            </Button>
          </Box>
        )}

        {/* Property cards */}
        {properties !== null && properties.length > 0 && (
          <Grid container spacing={2.5}>
            {properties.map((p) => (
              <Grid item xs={12} sm={6} md={4} key={p.id}>
                <Card sx={{ height: '100%' }}>
                  <CardActionArea
                    onClick={() => navigate(`/properties/${p.id}`)}
                    sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
                  >
                    <CardContent sx={{ width: '100%' }}>
                      {/* Icon */}
                      <Box sx={(theme) => ({
                        width: 40, height: 40, borderRadius: 2,
                        bgcolor: theme.palette.mode === 'dark'
                          ? 'rgba(37,99,235,0.18)'
                          : '#EFF6FF',
                        display: 'flex', alignItems: 'center',
                        justifyContent: 'center', mb: 2,
                      })}>
                        <HomeWorkOutlinedIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                      </Box>

                      <Typography
                        variant="h6"
                        sx={{
                          fontFamily: '"Fraunces", Georgia, serif',
                          fontWeight: 400,
                          fontSize: '1.1rem',
                          mb: 0.75,
                          lineHeight: 1.3,
                        }}
                      >
                        {p.name ?? 'Unnamed property'}
                      </Typography>

                      {p.address && (
                        <Stack direction="row" spacing={0.5} alignItems="flex-start">
                          <LocationOnOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary', mt: '3px', flexShrink: 0 }} />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ lineHeight: 1.4, fontSize: '0.8rem' }}
                          >
                            {p.address}
                          </Typography>
                        </Stack>
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}

            {/* Add property card */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={(theme) => ({
                  height: '100%',
                  border: '1px dashed',
                  borderColor: 'divider',
                  bgcolor: 'transparent',
                  boxShadow: 'none',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: theme.palette.mode === 'dark'
                      ? 'rgba(37,99,235,0.10)'
                      : '#EFF6FF',
                    transform: 'translateY(-1px)',
                    boxShadow: 'none',
                  },
                  cursor: 'pointer',
                  minHeight: 140,
                })}
                onClick={openDialog}
              >
                <CardContent sx={{
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center',
                  height: '100%', minHeight: 140,
                }}>
                  <AddIcon sx={{ fontSize: 28, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    Add property
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Container>

      {/* Add property dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={(e) => void handleAddProperty(e)}>
          <DialogTitle>Add property</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              <TextField
                label="Property name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                required
                fullWidth
                size="medium"
                autoFocus
                placeholder="e.g. Maple Street Duplex"
              />
              <TextField
                label="Address"
                value={formAddress}
                onChange={(e) => setFormAddress(e.target.value)}
                required
                fullWidth
                size="medium"
                placeholder="e.g. 123 Maple St, San Diego CA 92101"
              />
              {formError && <Alert severity="error">{formError}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)} disabled={submitting} color="inherit">
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create property'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
}
